import React, { useState, useEffect, useRef } from 'react';
import {
  CreditCard,
  Lock,
  AlertTriangle,
  Loader,
  User,
  MapPin,
  Eye,
  EyeOff,
} from 'lucide-react';
import type { Stripe } from '@stripe/stripe-js';
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement, Elements } from '@stripe/react-stripe-js';
import { getStripe, calculatePaymentSplit, formatCurrency, type PaymentBreakdown } from '../lib/stripe';

interface PaymentFormProps {
  bookingId?: string;
  amount: number;
  breakdown: {
    baseFare: number;
    distanceFare: number;
    vehicleFeatures: number;
    supportWorkerCost: number;
    peakTimeSurcharge?: number;
    bookingTypeDiscount?: number;
  };
  bookingData?: {
    pickup_address: string;
    dropoff_address: string;
    pickup_time: string;
    dropoff_time?: string | null;
    vehicle_features: string[];
    support_workers_count: number;
    fare_estimate: number;
    booking_type: 'on_demand' | 'scheduled' | 'advance';
    lead_time_hours: number;
    time_multiplier: number;
    booking_type_discount: number;
    special_requirements?: string | null;
    payment_method?: 'cash_bank' | 'stripe';
  };
  guestInfo?: {
    name: string;
    email: string;
    phone: string;
  };
  driverAccountId?: string;
  supportWorkerAccountIds?: string[];
  onSuccess: (paymentIntent: any) => void;
  onError: (error: string) => void;
  className?: string;
}

const PaymentFormInner: React.FC<PaymentFormProps> = ({
  bookingId,
  amount,
  breakdown,
  bookingData,
  guestInfo,
  driverAccountId,
  supportWorkerAccountIds = [],
  onSuccess,
  onError,
  className = ''
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [paymentBreakdown, setPaymentBreakdown] = useState<PaymentBreakdown | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const paymentIntentCreatedForBooking = useRef<string | null>(null);

  // Card element states
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);
  const [cardNumberError, setCardNumberError] = useState<string | null>(null);
  const [cardExpiryError, setCardExpiryError] = useState<string | null>(null);
  const [cardCvcError, setCardCvcError] = useState<string | null>(null);
  
  // Billing information
  const [billingInfo, setBillingInfo] = useState({
    name: guestInfo?.name || '',
    email: guestInfo?.email || '',
    postalCode: '',
    address: '',
    city: '',
    country: 'GB'
  });

  const [showBillingDetails, setShowBillingDetails] = useState(false);

  useEffect(() => {
    // Pre-fill billing info from guest data
    if (guestInfo) {
      setBillingInfo(prev => ({
        ...prev,
        name: guestInfo.name,
        email: guestInfo.email
      }));
    }
  }, [guestInfo]);

  useEffect(() => {
    const calculatedBreakdown = calculatePaymentSplit(
      breakdown.baseFare,
      breakdown.distanceFare,
      breakdown.vehicleFeatures,
      breakdown.supportWorkerCost,
      breakdown.peakTimeSurcharge || 0
    );
    setPaymentBreakdown(calculatedBreakdown);

    if (!bookingId) {
      paymentIntentCreatedForBooking.current = null;
      setClientSecret(null);
      return;
    }

    if (
      !calculatedBreakdown ||
      paymentIntentCreatedForBooking.current === bookingId
    ) {
      return;
    }

    paymentIntentCreatedForBooking.current = bookingId;
    void createPaymentIntent(bookingId, calculatedBreakdown);
  }, [bookingId, amount, breakdown, driverAccountId, supportWorkerAccountIds]);

  const createPaymentIntent = async (
    targetBookingId: string,
    breakdownForStripe: PaymentBreakdown
  ) => {
    try {
      console.log('Creating payment intent for booking:', targetBookingId);
      
      const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const base = String(import.meta.env.VITE_SUPABASE_URL || '').replace(/\/$/, '');
      const response = await fetch(`${base}/functions/v1/stripe-create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anon}`,
          apikey: anon || '',
        },
        body: JSON.stringify({
          booking_id: targetBookingId,
          amount: Math.round(amount * 100), // Convert to pence
          driver_account_id: driverAccountId,
          support_worker_account_ids: supportWorkerAccountIds,
          payment_breakdown: breakdownForStripe
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Payment setup failed (${response.status})`);
      }

      const { client_secret } = await response.json();
      console.log('Payment intent created successfully');
      setClientSecret(client_secret);
    } catch (error) {
      console.error('Payment intent creation error:', error);
      onError(error instanceof Error ? error.message : 'Payment setup failed');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      onError('Payment system not ready. Please try again.');
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      onError('Card element not found');
      return;
    }

    setProcessing(true);

    try {
      console.log('Confirming card payment...');
      
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            name: billingInfo.name,
            email: billingInfo.email,
            address: {
              postal_code: billingInfo.postalCode,
              line1: billingInfo.address || undefined,
              city: billingInfo.city || undefined,
              country: billingInfo.country,
            },
          },
        }
      });

      if (error) {
        console.error('Payment confirmation error:', error);
        onError(error.message || 'Payment failed');
      } else if (paymentIntent.status === 'succeeded') {
        console.log('Payment succeeded:', paymentIntent.id);

        // Status + emails handled by Stripe webhook (Supabase V1)
        onSuccess(paymentIntent);
      }
    } catch (err) {
      console.error('Payment processing error:', err);
      onError(err instanceof Error ? err.message : 'Payment processing failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleBillingInfoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setBillingInfo(prev => ({ ...prev, [name]: value }));
  };

  const isFormComplete = () => {
    return cardNumberComplete && 
           cardExpiryComplete && 
           cardCvcComplete && 
           billingInfo.name.trim() && 
           billingInfo.postalCode.trim() &&
           !cardNumberError && 
           !cardExpiryError && 
           !cardCvcError;
  };

  const elementOptions = {
    style: {
      base: {
        fontSize: '15px',
        color: '#1f2937',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontWeight: '400',
        lineHeight: '20px',
        fontSmoothing: 'antialiased',
        letterSpacing: '0.025em',
        '::placeholder': {
          color: '#9ca3af',
        },
        iconColor: '#6b7280',
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444',
      },
      complete: {
        color: '#059669',
        iconColor: '#059669',
      },
      empty: {
        color: '#9ca3af',
        iconColor: '#d1d5db',
      },
    },
    showIcon: true,
    iconStyle: 'default',
    hideIcon: false,
  };

  const payableTotal =
    paymentBreakdown
      ? paymentBreakdown.breakdown.baseFare +
        paymentBreakdown.breakdown.distanceFare +
        paymentBreakdown.breakdown.vehicleFeatures +
        paymentBreakdown.breakdown.supportWorkerCost +
        (paymentBreakdown.breakdown.peakTimeSurcharge || 0) +
        (breakdown.bookingTypeDiscount || 0)
      : amount;

  return (
    <div className={`rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}>
      <div className="flex items-start gap-2 border-b border-gray-100 px-4 py-3">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900">Card payment</h3>
          <p className="text-xs text-gray-500">
            Encrypted via Stripe. AbleGo does not store your card number.
          </p>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {paymentBreakdown && (
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
            <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
              Amount
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between gap-2">
                <span>Base + distance</span>
                <span className="tabular-nums text-gray-900">
                  {formatCurrency(
                    paymentBreakdown.breakdown.baseFare + paymentBreakdown.breakdown.distanceFare,
                  )}
                </span>
              </div>
              {paymentBreakdown.breakdown.vehicleFeatures > 0 && (
                <div className="flex justify-between gap-2">
                  <span>Vehicle add-ons</span>
                  <span className="tabular-nums text-gray-900">
                    {formatCurrency(paymentBreakdown.breakdown.vehicleFeatures)}
                  </span>
                </div>
              )}
              {paymentBreakdown.breakdown.supportWorkerCost > 0 && (
                <div className="flex justify-between gap-2">
                  <span>Support workers</span>
                  <span className="tabular-nums text-gray-900">
                    {formatCurrency(paymentBreakdown.breakdown.supportWorkerCost)}
                  </span>
                </div>
              )}
              {paymentBreakdown.breakdown.peakTimeSurcharge > 0 && (
                <div className="flex justify-between gap-2">
                  <span>Peak surcharge</span>
                  <span className="tabular-nums text-gray-900">
                    {formatCurrency(paymentBreakdown.breakdown.peakTimeSurcharge)}
                  </span>
                </div>
              )}
              {breakdown.bookingTypeDiscount !== 0 && (
                <div className="flex justify-between gap-2">
                  <span>
                    {breakdown.bookingTypeDiscount > 0 ? 'On-demand surcharge' : 'Advance discount'}
                  </span>
                  <span className="tabular-nums text-gray-900">
                    {breakdown.bookingTypeDiscount > 0 ? '+' : ''}
                    {formatCurrency(Math.abs(breakdown.bookingTypeDiscount))}
                  </span>
                </div>
              )}
            </div>
            <div className="mt-2 flex items-baseline justify-between border-t border-gray-200 pt-2">
              <span className="text-sm font-semibold text-gray-900">Total</span>
              <span className="text-lg font-semibold tabular-nums text-gray-900">
                {formatCurrency(payableTotal)}
              </span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="mb-3 flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-gray-800">Card</span>
              <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                Visa · Mastercard · Amex
              </span>
            </div>

            <div className="mb-3">
              <label className="mb-1.5 block text-xs font-medium text-gray-600">Number</label>
              <div
                className={`rounded-lg border bg-white px-3 py-2.5 transition-colors ${
                  cardNumberError
                    ? 'border-red-300 ring-1 ring-red-100'
                    : 'border-gray-200 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-100'
                }`}
              >
                <CardNumberElement
                  options={{
                    ...elementOptions,
                    placeholder: '1234 5678 9012 3456',
                  }}
                  onChange={(event) => {
                    setCardNumberComplete(event.complete);
                    setCardNumberError(event.error ? event.error.message : null);
                  }}
                />
              </div>
              {cardNumberError && (
                <div className="mt-1 flex items-center gap-1 text-xs text-red-600">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  {cardNumberError}
                </div>
              )}
            </div>

            <div className="mb-1 grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-600">Expiry</label>
                <div
                  className={`rounded-lg border bg-white px-3 py-2.5 ${
                    cardExpiryError
                      ? 'border-red-300 ring-1 ring-red-100'
                      : 'border-gray-200 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-100'
                  }`}
                >
                  <CardExpiryElement
                    options={{
                      ...elementOptions,
                      placeholder: 'MM / YY',
                    }}
                    onChange={(event) => {
                      setCardExpiryComplete(event.complete);
                      setCardExpiryError(event.error ? event.error.message : null);
                    }}
                  />
                </div>
                {cardExpiryError && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-red-600">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {cardExpiryError}
                  </div>
                )}
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-600">CVC</label>
                <div
                  className={`rounded-lg border bg-white px-3 py-2.5 ${
                    cardCvcError
                      ? 'border-red-300 ring-1 ring-red-100'
                      : 'border-gray-200 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-100'
                  }`}
                >
                  <CardCvcElement
                    options={{
                      ...elementOptions,
                      placeholder: '123',
                    }}
                    onChange={(event) => {
                      setCardCvcComplete(event.complete);
                      setCardCvcError(event.error ? event.error.message : null);
                    }}
                  />
                </div>
                {cardCvcError && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-red-600">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {cardCvcError}
                  </div>
                )}
              </div>
            </div>
            <p className="text-[11px] leading-snug text-gray-400">
              CVC is usually 3 digits on the back; Amex uses 4 on the front.
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 p-3">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-800">Billing</span>
              <button
                type="button"
                onClick={() => setShowBillingDetails(!showBillingDetails)}
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                {showBillingDetails ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {showBillingDetails ? 'Hide extras' : 'More address fields'}
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-600">
                  Name on card
                </label>
                <div className="relative">
                  <User className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="name"
                    value={billingInfo.name}
                    onChange={handleBillingInfoChange}
                    required
                    className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
                    placeholder="As on card"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-600">
                  Postcode
                </label>
                <div className="relative">
                  <MapPin className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="postalCode"
                    value={billingInfo.postalCode}
                    onChange={handleBillingInfoChange}
                    required
                    className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
                    placeholder="e.g. SW1A 1AA"
                  />
                </div>
              </div>
            </div>

            {showBillingDetails && (
              <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-600">
                    Address (optional)
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={billingInfo.address}
                    onChange={handleBillingInfoChange}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
                    placeholder="Street"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-gray-600">
                      City (optional)
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={billingInfo.city}
                      onChange={handleBillingInfoChange}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-gray-600">Country</label>
                    <select
                      name="country"
                      value={billingInfo.country}
                      onChange={handleBillingInfoChange}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
                    >
                      <option value="GB">United Kingdom</option>
                      <option value="IE">Ireland</option>
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                      <option value="AU">Australia</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          <p className="text-center text-xs text-gray-500">
            We&apos;ll confirm by email when payment succeeds.
          </p>

          <div className="space-y-2">
            <button
              type="submit"
              disabled={!stripe || processing || !clientSecret || !paymentBreakdown || !isFormComplete()}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-gray-900 py-3 text-sm font-semibold text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {processing ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Processing…
                </>
              ) : !isFormComplete() ? (
                <>
                  <CreditCard className="h-4 w-4 opacity-70" />
                  Fill card details
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4 opacity-80" />
                  Pay {formatCurrency(payableTotal)}
                </>
              )}
            </button>
            <p className="text-center text-[11px] text-gray-400">Secured by Stripe · TLS</p>
          </div>
        </form>
      </div>
    </div>
  );
};

const PaymentForm: React.FC<PaymentFormProps> = (props) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [stripeError, setStripeError] = useState<string | null>(null);

  useEffect(() => {
    // Initialize Stripe
    const initializeStripe = async () => {
      try {
        const stripe = await getStripe();
        if (stripe) {
          setStripePromise(Promise.resolve(stripe));
          console.log('✅ Stripe loaded successfully for payment form');
        } else {
          setStripeError('Payment system not configured. Please contact support.');
        }
      } catch (error) {
        setStripeError('Failed to initialize payment system.');
        console.error('Stripe initialization error:', error);
      }
    };

    initializeStripe();
  }, []);

  if (!stripePromise) {
    return (
      <div
        className={`flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm ${props.className}`}
      >
        <Loader className="h-5 w-5 shrink-0 animate-spin text-gray-500" />
        <div className="min-w-0 text-sm">
          <p className="font-medium text-gray-900">Loading payment</p>
          <p className="text-xs text-gray-500">One moment…</p>
        </div>
      </div>
    );
  }

  if (stripeError) {
    return (
      <div
        className={`rounded-xl border border-red-200 bg-red-50/50 p-4 text-center shadow-sm ${props.className}`}
      >
        <AlertTriangle className="mx-auto mb-2 h-5 w-5 text-red-600" />
        <p className="text-sm font-medium text-gray-900">Payments unavailable</p>
        <p className="mt-1 text-xs text-gray-600">{stripeError}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-3 rounded-lg bg-gray-900 px-4 py-2 text-xs font-medium text-white hover:bg-gray-800"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <PaymentFormInner {...props} />
    </Elements>
  );
};

export default PaymentForm;